import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, TYPE_META, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, MapPin, Trash2, Tag, FolderOpen, Users, CheckCircle2 } from "@/lib/icons";

export default function CaptureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cap, setCap] = useState(null);
  const [opts, setOpts] = useState({ collections: [], people: [], places: [] });
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");

  const load = useCallback(() => {
    api.get(`/captures/${id}`).then((r) => {
      setCap(r.data);
      setTitle(r.data.title || "");
      setText(r.data.text || "");
      setTags((r.data.tags || []).join(", "));
    }).catch(() => navigate("/catalog"));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/collections"), api.get("/people"), api.get("/places")]).then(([c, p, pl]) =>
      setOpts({ collections: c.data.collections, people: p.data.people, places: pl.data.places })
    ).catch(() => {});
  }, []);

  if (!cap) return <div className="text-slate-500">Loading…</div>;
  const meta = TYPE_META[cap.type] || TYPE_META.note;

  const saveMeta = async () => {
    await api.patch(`/captures/${id}`, { title, text, tags: tags.split(",").map((t) => t.trim()).filter(Boolean) });
    toast.success("Saved");
    load();
  };
  const addRel = async (target_type, target_id) => {
    if (!target_id) return;
    await api.post(`/captures/${id}/relationships`, { target_type, target_id });
    toast.success(`Linked ${target_type}`);
    load();
  };
  const removeRel = async (relId) => { await api.delete(`/relationships/${relId}`); load(); };
  const setReview = async (state) => { await api.post(`/captures/${id}/review`, { review_state: state }); toast.success("Updated"); load(); };
  const del = async () => {
    if (!window.confirm("Move this capture to Trash? Links and originals are kept and can be restored.")) return;
    await api.delete(`/captures/${id}`);
    toast.success("Moved to Trash");
    navigate("/catalog");
  };

  const imageAsset = cap.assets.find((a) => a.mime_type?.startsWith("image/"));
  const audioAsset = cap.assets.find((a) => a.mime_type?.startsWith("audio/"));
  const videoAsset = cap.assets.find((a) => a.mime_type?.startsWith("video/"));

  return (
    <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: meta.color + "22", color: meta.color }}><meta.icon size={18} /></span>
          <StatusBadge state={cap.review_state} />
          {cap.assets.some((a) => a.is_duplicate) && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#F59E0B22", color: "#F59E0B" }}>Duplicate detected</span>}
        </div>

        {/* Media preview */}
        <div className="dl-card overflow-hidden">
          {imageAsset ? (
            <img src={assetUrl(imageAsset.id)} alt={cap.title} className="w-full max-h-[460px] object-contain bg-black/40" data-testid="detail-image" />
          ) : videoAsset ? (
            <video src={assetUrl(videoAsset.id)} controls className="w-full bg-black" />
          ) : audioAsset ? (
            <div className="p-6"><audio src={assetUrl(audioAsset.id)} controls className="w-full" data-testid="detail-audio" />
              <p className="text-xs text-slate-500 mt-2">Audio preserved. Transcription is an optional Stage 3 feature — not performed here.</p>
            </div>
          ) : cap.type === "link" && cap.source_url ? (
            <a href={cap.source_url} target="_blank" rel="noreferrer" className="block p-6 text-sky-400 break-all hover:underline" data-testid="detail-link">{cap.source_url}</a>
          ) : cap.assets.length > 0 ? (
            <div className="p-8 text-center text-slate-400">
              <FolderOpen size={32} className="mx-auto mb-2 text-slate-500" />
              Preview not supported for this file type. The original is preserved and downloadable below.
            </div>
          ) : (
            <div className="p-6 whitespace-pre-wrap text-slate-200">{cap.text || <span className="text-slate-500">No text content.</span>}</div>
          )}
        </div>

        {/* Attachments / originals */}
        {cap.assets.length > 0 && (
          <div className="dl-card p-4 space-y-2">
            <div className="font-mono-label text-[11px] text-slate-500">Original files</div>
            {cap.assets.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <div className="text-white truncate">{a.original_filename}</div>
                  <div className="text-[11px] text-slate-500">{a.mime_type} · {(a.byte_size / 1024).toFixed(0)} KB · {a.upload_state}</div>
                </div>
                <a href={assetUrl(a.id)} download={a.original_filename} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="gap-1.5" data-testid={`download-asset-${a.id}`}><Download size={14} /> Download</Button>
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Editable metadata */}
        <div className="dl-card p-4 space-y-3">
          <div className="font-mono-label text-[11px] text-slate-500">Details</div>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="bg-black/20" data-testid="detail-title-input" />
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Notes / description" className="bg-black/20 min-h-[80px]" data-testid="detail-text-input" />
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-slate-500" />
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags, comma separated" className="bg-black/20" data-testid="detail-tags-input" />
          </div>
          <Button size="sm" onClick={saveMeta} style={{ background: "#2563EB" }} data-testid="save-detail-btn">Save changes</Button>
        </div>

        {/* Activity */}
        {cap.activity?.length > 0 && (
          <div className="dl-card p-4">
            <div className="font-mono-label text-[11px] text-slate-500 mb-2">Activity</div>
            <div className="space-y-1.5">
              {cap.activity.map((a) => (
                <div key={a.id} className="text-xs text-slate-400"><span className="text-slate-300">{a.user_name}</span> {a.action} · {fmtDate(a.created_at)}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Side panel */}
      <aside className="space-y-4">
        <div className="dl-card p-4 space-y-2">
          <div className="font-mono-label text-[11px] text-slate-500">Review state</div>
          <div className="flex gap-2">
            {["inbox", "reviewed", "archived"].map((s) => (
              <Button key={s} size="sm" variant={cap.review_state === s ? "default" : "outline"} onClick={() => setReview(s)}
                data-testid={`review-${s}-btn`} className="capitalize flex-1 text-xs" style={cap.review_state === s ? { background: "#10B981" } : {}}>
                {s === "inbox" ? "Dock" : s}
              </Button>
            ))}
          </div>
        </div>

        <div className="dl-card p-4 space-y-3">
          <div className="font-mono-label text-[11px] text-slate-500">Connect</div>
          <LinkPicker label="Add to collection" icon={FolderOpen} options={opts.collections} onPick={(v) => addRel("collection", v)} testid="link-collection" />
          <LinkPicker label="Link person" icon={Users} options={opts.people} onPick={(v) => addRel("person", v)} testid="link-person" />
          <LinkPicker label="Link place" icon={MapPin} options={opts.places} onPick={(v) => addRel("place", v)} testid="link-place" />
        </div>

        {cap.relationships?.length > 0 && (
          <div className="dl-card p-4 space-y-2">
            <div className="font-mono-label text-[11px] text-slate-500">Linked</div>
            {cap.relationships.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm">
                <span className="text-slate-200 truncate">{r.target_name || r.target_id} <span className="text-[10px] text-slate-500">· {r.target_type}</span></span>
                <button onClick={() => removeRel(r.id)} className="text-slate-500 hover:text-red-400 text-xs" data-testid={`unlink-${r.id}`}>remove</button>
              </div>
            ))}
          </div>
        )}

        {cap.location && (
          <div className="dl-card p-4 text-sm text-slate-300 flex items-center gap-2">
            <MapPin size={15} className="text-teal-400" /> {cap.location.label || `${cap.location.lat}, ${cap.location.lng}`}
          </div>
        )}

        <div className="dl-card p-4 text-xs text-slate-500 space-y-1">
          <div>Captured {fmtDate(cap.capture_time)}</div>
          <div>Added {fmtDate(cap.created_at)}</div>
          <div>By {cap.author_name}</div>
        </div>

        <Button variant="outline" onClick={del} className="w-full gap-2 text-red-400 border-red-400/30 hover:bg-red-400/10" data-testid="delete-capture-btn">
          <Trash2 size={15} /> Move to Trash
        </Button>
      </aside>
    </div>
  );
}

function LinkPicker({ label, icon: Icon, options, onPick, testid }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-slate-500 shrink-0" />
      <Select value={v} onValueChange={(val) => { onPick(val); setV(""); }}>
        <SelectTrigger data-testid={testid} className="bg-black/20 text-xs h-9"><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent>
          {options.length === 0 ? <SelectItem value="none" disabled>None yet</SelectItem> :
            options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
